import React, { useContext, useMemo, useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AutoSuggest, Input, Select } from "Components/Form";
import Checkbox from "Components/Form/Checkbox";
import SelectMultiple from "Components/Form/SelectMultiple";
import SkeletonComponent from "Components/Generic/Skeleton";
import useYupValidation from "Hooks/useYupValidation";
import { MemberUser } from "Models/members/member.user";
import { User } from "Models/user";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ApplicationsService from "Services/applications.service";
import MembersService from "Services/members.service";
import * as yup from "yup";

import { useAppState } from "Context/StateContext";
import { Status, UserTypes } from "Util/enums";
import { TypeOfMember } from "Util/enums/members";
import { ApplicationKeys, MemberKeys } from "Util/queries";
import { getTypes } from "../../../../Users/types";
import { MemberContext } from "../../context";
import { Wrapper } from "./styles";
import GroupService from "Services/group.service";
import { Group } from "Models/group";

type Props = {
  errors?: yup.ValidationError[];
};

export function UserDetails({ errors = [] }: Props) {
  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView: boolean }>();
  const { member, handleUpdateMember } = useContext(MemberContext);
  const { currentUser, isFMVersion, isMovingVersion } = useAppState();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [memberUsername, setMemberUsername] = useState<string>("");
  const [manager, setManager] = useState<User | undefined>(member?.userCompany?.manager);

  const { data: groupList, isFetching: fetchingGroupList } = useQuery({
    queryKey: ["groups.list"],
    queryFn: async () => {
      const response = await GroupService.getGroups({
        status: 1,
        companyId: currentUser?.companyId,
      });

      if (response && response?.data) {
        const orderedData = response.data?.sort((a: any, b: any) => {
          const nameA = a?.name?.toUpperCase();
          const nameB = b?.name?.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        return orderedData;
      }

      return [];
    },
    refetchOnWindowFocus: false,
  });

  // const { data: rolesList, isFetching: fetchingRoleList } = useQuery({
  //   queryKey: RoleKeys.getAll([]),
  //   queryFn: async () => {
  //     const response = await RoleService.getAll({
  //       status: Status.ACTIVE,
  //       companyId: currentUser?.companyId,
  //     });

  //     if (response && response?.data) return response.data;

  //     return [];
  //   },
  //   refetchOnWindowFocus: false,
  // });

  const { data: applicationsList, isFetching: fetchingApplicationList } = useQuery({
    queryKey: ApplicationKeys.getAll([]),
    queryFn: async () => {
      const response = await ApplicationsService.getAll({
        include: JSON.stringify([
          {
            association: "profiles",
            where: {
              initials: currentUser?.company?.profile?.initials,
            },
          },
        ]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const { data: managersList, isFetching: fetchingManagersList } = useQuery({
    queryKey: MemberKeys.getAll(["people-member-create-modal"]),
    queryFn: async () => {
      const response = await MembersService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["fullName", "asc"]]),
        typeOfMember: TypeOfMember.USER,
        include: JSON.stringify([
          {
            association: "user",
            attributes: ["id", "fullName"],
          },
        ]),
      });

      if (response?.data && Array.isArray(response.data)) return response.data as MemberUser[];

      return [];
    },
    refetchOnWindowFocus: false,
  });

  const renderTypeOfUser = () => {
    const types = getTypes(isMovingVersion, isFMVersion);

    return types
      ?.filter((type) => type.name !== UserTypes.STAFF)
      .map((type) => {
        return (
          <MenuItem data-cy={type.name} key={type.name} value={type.value}>
            {type.name}
          </MenuItem>
        );
      });
  };

  const disabledUsernameField = useMemo(() => {
    const currentMember = member as MemberUser;

    if (currentMember?.email?.trim().length) {
      if (currentMember?.email === currentMember?.username) {
        return true;
      }

      return false;
    }

    return false;
  }, [member]);

  const memberGroupSelected = useMemo(() => {
    return groupList?.find((group) => group?.id === (member as MemberUser)?.groupId)?.name;
  }, [groupList, member]);

  const SPACES = member?.id ? 3 : 4;

  return (
    <Wrapper>
      <Grid container spacing={2}>
        <Grid
          item
          container
          xs={12}
          spacing={2}
          alignItems={errors?.length ? "flex-start" : "flex-end"}
        >
          <Grid item xs={12} md={SPACES}>
            <Input
              disabled={location?.state?.isView || disabledUsernameField}
              label={translate("username")}
              value={(member as MemberUser)?.username}
              onChange={(event) => handleUpdateMember("username", event.target.value)}
              required
              error={fieldHasError("username")}
              errorMessage={getErrorByField("username")}
              placeholder={translate("mailmailcom")}
            />
          </Grid>
          <Grid item xs={12} md={SPACES}>
            <Input
              disabled={location?.state?.isView}
              required
              error={fieldHasError("code")}
              errorMessage={getErrorByField("code")}
              label={translate("code")}
              value={(member as MemberUser)?.code}
              autoComplete="off"
              onChange={(event) => handleUpdateMember("code", event.target.value)}
              placeholder={translate("typeHere")}
            />
          </Grid>
          <Grid item xs={12} md={SPACES}>
            <AutoSuggest
              disabled={location?.state?.isView ?? fetchingGroupList}
              label={translate("group")}
              required
              options={groupList ?? []}
              inputValue={memberGroupSelected}
              getOptionLabel={(option: Group) =>
                option?.name ? `${option?.name} - ${option?.name}` : ""
              }
              value={(member as MemberUser)?.groupId}
              getOptionSelected={(option: Group, value: Group) => option?.id === value?.id}
              // getOptionKey={(option) => `${option.id}`}
              onChange={(group: Group) => {
                handleUpdateMember("groupId", Number(group?.id));
              }}
              error={fieldHasError("groupId")}
              errorMessage={getErrorByField("groupId")}
              placeholder={translate("select")}
            />
            {/* <SkeletonComponent isLoading={fetchingGroupList}>
              <Select
                disabled={location?.state?.isView}
                label={translate("group")}
                required
                value={(member as MemberUser)?.groupId}
                onChange={(event) => handleUpdateMember("groupId", Number(event.target.value))}
                error={fieldHasError("groupId")}
                errorMessage={getErrorByField("groupId")}
                placeholder={translate("select")}
              >
                {groupList?.map((group) => (
                  <MenuItem key={group?.id} value={group?.id}>
                    {group?.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent> */}
          </Grid>
        </Grid>
        <Grid item container xs={12}>
          <Checkbox
            disabled={location?.state?.isView || !member?.email}
            label={translate("useEmailAddress")}
            checked={member?.email ? member.email === (member as MemberUser)?.username : false}
            onChange={(_, checked) => {
              if (checked && member?.email) {
                setMemberUsername((member as MemberUser)?.username || "");
                handleUpdateMember("username", member?.email);
              } else if (!checked) {
                if (memberUsername === member?.email) handleUpdateMember("username", "");
                else handleUpdateMember("username", memberUsername);
              }
            }}
            placeholder={translate("select")}
          />
        </Grid>
        <Grid
          item
          container
          xs={12}
          spacing={2}
          alignItems={errors?.length ? "flex-start" : "flex-end"}
        >
          <Grid item xs={12} md={SPACES}>
            <Select
              disabled={location?.state?.isView}
              label={translate("typerOfUser")}
              required
              value={(member as MemberUser)?.typeOfUser}
              onChange={(event) => handleUpdateMember("typeOfUser", event.target.value)}
              error={fieldHasError("typeOfUser")}
              errorMessage={getErrorByField("typeOfUser")}
              placeholder={translate("select")}
            >
              {renderTypeOfUser()}
            </Select>
          </Grid>
          <Grid item xs={12} md={SPACES}>
            <SkeletonComponent isLoading={fetchingApplicationList}>
              <SelectMultiple
                disabled={location?.state?.isView}
                name="accessTo"
                label={translate("accessTo")}
                value={(member as MemberUser)?.applications?.map((option) => option.id || -2) || []}
                selecteds={
                  (member as MemberUser)?.applications?.map((option) => ({
                    key: Number(option.id),
                    label: translate(option.name),
                  })) || []
                }
                renderInternalCount={false}
                onChange={(event: SelectChangeEvent<any>) => {
                  if (event.target.value.includes(-2) || event.target.value.includes(0)) return;

                  const options = applicationsList?.filter((option) =>
                    event.target.value.includes(option.id)
                  );

                  const applicationsLength = (member as MemberUser)?.applications?.length ?? 0;

                  if (options && applicationsLength !== options.length) {
                    handleUpdateMember("applications", options);
                  }
                }}
                onAddAll={() => {
                  handleUpdateMember("applications", applicationsList);
                }}
                onRemoveAll={() => {
                  handleUpdateMember("applications", []);
                }}
                items={
                  applicationsList?.map((option) => ({
                    key: Number(option.id),
                    label: translate(option.name),
                  })) || []
                }
                placeholder={translate("select")}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={12} md={SPACES}>
            <AutoSuggest
              disabled={location?.state?.isView ?? fetchingManagersList}
              label={translate("manager")}
              options={managersList?.length ? managersList : []}
              inputValue={manager?.fullName || ""}
              getOptionLabel={(option: MemberUser) => option?.fullName ?? ""}
              value={manager?.id}
              getOptionSelected={(option: MemberUser, value: MemberUser) =>
                option?.id === value?.id
              }
              onChange={(memberManager?: MemberUser) => {
                handleUpdateMember("managerId", memberManager?.userId);
                handleUpdateMember("manager", memberManager);
                setManager(memberManager?.user as User);
              }}
              placeholder={translate("select")}
            />
          </Grid>
          <Grid item xs={12} container alignItems="center" columnSpacing={4}>
            <Grid item>
              <Checkbox
                disabled={location?.state?.isView}
                label={translate("thisUserIsAlsoALeader")}
                data-cy="checkboxLeader"
                checked={!!(member as MemberUser)?.leader}
                onChange={(_, checked) => {
                  handleUpdateMember("leader", checked);
                }}
              />
            </Grid>

            <Grid item>
              <Checkbox
                disabled={location?.state?.isView}
                label={translate("displayInDispatchModule")}
                data-cy="checkboxDisplayInDispatch"
                checked={!!(member as MemberUser)?.displayInDispatch}
                onChange={(_, checked) => {
                  handleUpdateMember("displayInDispatch", checked);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Wrapper>
  );
}
